import React from 'react';
import { ProgressBar } from '../ui/ProgressBar';
import { CorporatePanel } from '../ui/CorporatePanel';
import { calculateVPD } from '../../game/systems/environmental';
import { DeskSurface } from '../facility/DeskSurface';

export function FacilityScreen({ state, dispatch }) {
  const selectedRoom = state.rooms.find(r => r.id === state.ui.selectedRoomId);

  return (
    <div className="screen" style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 14, height: '100%', overflow: 'hidden' }}>

      {/* LEFT — CEO Desk */}
      <DeskSurface state={state} dispatch={dispatch} />

      {/* RIGHT — Selected room quick stats */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, overflow: 'auto' }}>

        {selectedRoom && selectedRoom.unlocked ? (
          <>
            <div className="panel">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{selectedRoom.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'capitalize' }}>{selectedRoom.type}</div>
                </div>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => dispatch({ type: 'NAVIGATE', payload: { screen: 'room', roomId: selectedRoom.id } })}
                >
                  Manage →
                </button>
              </div>

              {/* Environment */}
              <div className="panel-title">Environment</div>
              {(selectedRoom.environment ? [
                { label: '🌡️ Temp', val: `${selectedRoom.environment.temperature.toFixed(1)}°C`, target: `${selectedRoom.recipe?.temperature}°C` },
                { label: '💧 Humidity', val: `${selectedRoom.environment.humidity.toFixed(0)}%`, target: `${selectedRoom.recipe?.humidity}%` },
                { label: '💨 VPD', val: `${calculateVPD(selectedRoom.environment.temperature, selectedRoom.environment.humidity).toFixed(2)} kPa` },
                { label: '🟢 CO₂', val: `${Math.round(selectedRoom.environment.co2 ?? 0)} ppm` },
              ] : []).map(({ label, val, target }) => (
                <div key={label} className="stat-row">
                  <span className="lbl">{label}</span>
                  <span>
                    <span className="val">{val}</span>
                    {target && <span style={{ color: 'var(--text-dim)', fontSize: 11, marginLeft: 4 }}>/ {target}</span>}
                  </span>
                </div>
              ))}
            </div>

            {selectedRoom.batch ? (
              <div className="panel">
                <div className="panel-title">Active Batch</div>
                <div style={{ marginBottom: 8 }}>
                  <span className={`batch-stage-badge ${selectedRoom.batch.stage}`}>
                    {selectedRoom.batch.stage}
                  </span>
                </div>
                <div className="stat-row">
                  <span className="lbl">Strain</span>
                  <span className="val" style={{ fontSize: 12 }}>{selectedRoom.batch.strainId.replace(/-/g, ' ')}</span>
                </div>
                <div className="stat-row">
                  <span className="lbl">Day in Stage</span>
                  <span className="val">{Math.floor(selectedRoom.batch.dayInStage)}</span>
                </div>
                <div className="stat-row">
                  <span className="lbl">Plants</span>
                  <span className="val">{selectedRoom.batch.plantCount}</span>
                </div>

                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>Health {Math.round(selectedRoom.batch.health)}%</div>
                  <ProgressBar value={selectedRoom.batch.health} />
                </div>
                <div style={{ marginTop: 6 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>Yield Potential {Math.round(selectedRoom.batch.yieldPotential)}%</div>
                  <ProgressBar value={selectedRoom.batch.yieldPotential} />
                </div>

                <div className="stat-row" style={{ marginTop: 8 }}>
                  <span className="lbl">Projected Yield</span>
                  <span className="val text-green">{selectedRoom.batch.projectedGrams}g</span>
                </div>

                {selectedRoom.batch.stage === 'harvest' && (
                  <button
                    className="btn btn-primary"
                    style={{ width: '100%', marginTop: 10 }}
                    onClick={() => dispatch({ type: 'HARVEST_BATCH', payload: { roomId: selectedRoom.id } })}
                  >
                    ✂️ Harvest Now
                  </button>
                )}
              </div>
            ) : (
              <div className="panel">
                <div className="panel-title">Batch</div>
                <div className="empty-state" style={{ padding: 16 }}>
                  <span className="empty-icon">🪴</span>
                  <span className="empty-title">Room Empty</span>
                  <button
                    className="btn btn-primary btn-sm"
                    style={{ marginTop: 8 }}
                    onClick={() => dispatch({ type: 'NAVIGATE', payload: { screen: 'room', roomId: selectedRoom.id } })}
                  >
                    Plant Batch
                  </button>
                </div>
              </div>
            )}

            {/* Equipment health */}
            <div className="panel">
              <div className="panel-title">Equipment</div>
              {[
                { key: 'hvac',   label: '⚙️ HVAC',   icon: 'equipment-hvac' },
                { key: 'lights', label: '💡 Lights',  icon: 'equipment-led' },
                { key: 'irrigation', label: '💧 Irrigation' },
              ].map(({ key, label }) => {
                const equip = selectedRoom.equipment[key];
                if (!equip) return null;
                const health = equip.health;
                return (
                  <div key={key} style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
                      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                      <span style={{ color: health < 40 ? 'var(--red)' : health < 70 ? 'var(--yellow)' : 'var(--green)', fontWeight: 600 }}>
                        {Math.round(health)}%
                        {health < 70 && (
                          <button
                            className="btn btn-warn btn-sm"
                            style={{ marginLeft: 6, padding: '1px 5px', fontSize: 10 }}
                            onClick={() => dispatch({ type: 'REPAIR_EQUIPMENT', payload: { roomId: selectedRoom.id, equipType: key } })}
                            disabled={state.cash < 200}
                          >
                            Repair $200
                          </button>
                        )}
                      </span>
                    </div>
                    <ProgressBar value={health} />
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="panel">
            <div className="empty-state">
              <span className="empty-icon">🏭</span>
              <span className="empty-title">Select a Room</span>
              <span className="empty-desc">Click any room in the facility view to inspect it.</span>
            </div>
          </div>
        )}

        {/* Weekly finances */}
        <div className="panel">
          <div className="panel-title">Weekly Summary</div>
          <div className="stat-row">
            <span className="lbl">Revenue</span>
            <span className="val text-green">${state.totalRevenue.toLocaleString()}</span>
          </div>
          <div className="stat-row">
            <span className="lbl">Expenses</span>
            <span className="val text-red">${state.totalExpenses.toLocaleString()}</span>
          </div>
          <div className="stat-row">
            <span className="lbl">Weekly Burn</span>
            <span className="val text-yellow">${state.weeklyBurn.toLocaleString()}</span>
          </div>
          <div className="stat-row">
            <span className="lbl">Research RP</span>
            <span className="val text-blue">{state.researchPoints} RP</span>
          </div>
        </div>

        {/* CannCorp panel — only visible when corporate investment is active */}
        <CorporatePanel state={state} dispatch={dispatch} />
      </div>
    </div>
  );
}
